import { useEffect, useState } from "react";

export const SYSTEM_STATUS_PAGE_URL = "https://status.strawberry.me/";

const STATUS_SUMMARY_URL = `${SYSTEM_STATUS_PAGE_URL}api/v1/summary`;

export type SystemStatusImpact =
    | "degraded_performance"
    | "partial_outage"
    | "full_outage";

export type OngoingIncident = {
    name: string;
    status: string;
    current_worst_impact: SystemStatusImpact;
    url: string;
};

export type StatusPageMaintenance = {
    name: string;
    status: string;
    starts_at?: string;
    ends_at?: string;
    url: string;
};

type SystemStatusSummary = {
    ongoing_incidents?: OngoingIncident[];
    in_progress_maintenances?: StatusPageMaintenance[];
    scheduled_maintenances?: StatusPageMaintenance[];
};

const IMPACTS_WORST_FIRST: SystemStatusImpact[] = [
    "full_outage",
    "partial_outage",
    "degraded_performance",
];

export default function useSystemStatus() {
    const [summary, setSummary] = useState<SystemStatusSummary>({});

    useEffect(() => {
        let cancelled = false;
        /* Native fetch instead of axios keeps this third-party call free of
           our shared axios defaults (X-Requested-With header, interceptors). */
        fetch(STATUS_SUMMARY_URL)
            .then((response) => response.json())
            .then((data: SystemStatusSummary) => {
                if (!cancelled && data && typeof data === "object") {
                    setSummary(data);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    const ongoingIncidents = summary.ongoing_incidents ?? [];
    const worstOngoingImpact =
        IMPACTS_WORST_FIRST.find((impact) =>
            ongoingIncidents.some(
                (incident) => incident.current_worst_impact === impact,
            ),
        ) ?? null;

    return {
        ongoingIncidents,
        hasOngoingIncident: ongoingIncidents.length > 0,
        worstOngoingImpact,
        inProgressMaintenances: summary.in_progress_maintenances ?? [],
        scheduledMaintenances: summary.scheduled_maintenances ?? [],
    };
}
